/*
 * Copyright (c) 2021-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: CurrentSelectionPanel.tsx
 * Project: mle-client-n
 * Committed last: 2022/1/18 @ 954
 * ---
 * Description:
 */

import { Icon, Intent, NonIdealState, Tab, Tabs, Tag } from '@blueprintjs/core';
import React, { useEffect, useState }                  from 'react';
import {
  GeneralControlsTab
}                                                      from './GeneralControlsTab';
import {
  shapeTypesWithData
}                                                      from '../../../common/selection-properties';
import {
  currentThemeIsDark,
  getShapeTypeFromId,
  shapeHumanReadableNameAssociation,
  shapeIconAssociation
}                                                      from '../../../common/utils';
import classNames                                      from 'classnames';
import {
  Editor
}                                                      from '../../../store/observables/Editor';
import { observer }                                    from 'mobx-react';
import {
  OptionsTab
}                                                      from '../selection-tabs/options/OptionsTab';
import {
  DataControlsTab
}                                                      from '../selection-tabs/data/DataControlsTab';

enum ActiveTabIds {
  GENERAL = 'generale',
  DATA = 'dati',
  OPTIONS = 'opzioni'
}

export interface ActiveTabProps {
  editor: Editor;
}

export interface CurrentSelectionPanelProps {
  editor: Editor;
}

export const CurrentSelectionPanel = observer((props: CurrentSelectionPanelProps) => {
  const [ activeTabId, setActiveTabId ] = useState(ActiveTabIds.GENERAL);

  useEffect(() => {
    setActiveTabId(ActiveTabIds.GENERAL);
  }, [ props.editor.getSelectedShape() ]);

  if (props.editor.hasSelection()) {
    return (
      <div className="mle-side-panel-wrapper overflow-clip"
           style={ { zIndex: 1 } }>
        <div
          className={ classNames(
            'mb-4 p-1 rounded flex flex-row align-middle gap-x-2',
            {
              'bg-bpdisabled-light': !currentThemeIsDark(),
              'bg-bpdisabled-dark': currentThemeIsDark()
            }) }>
          <Tag intent={ Intent.SUCCESS }
               icon={ shapeIconAssociation[getShapeTypeFromId(props.editor.getSelectedShape()?.pamelaShape__nonObservable.id() as string)] }>
            { shapeHumanReadableNameAssociation[getShapeTypeFromId(props.editor.getSelectedShape()?.pamelaShape__nonObservable.id() as string)] }
          </Tag>
          <Tag intent={ Intent.NONE }>
            { props.editor.getSelectedShape()?.pamelaShape__nonObservable.id() }
          </Tag>
        </div>
        <Tabs
          onChange={ newTabId => setActiveTabId(newTabId as ActiveTabIds) }
          selectedTabId={ activeTabId }
          animate
        >
          <Tab
            className="outline-none"
            id={ ActiveTabIds.GENERAL }
            title={ <div className="capitalize">
              <Icon size={ 12 } icon="layout-grid"
                    className="bp4-text-muted mr-1 mb-0.5"/>
              { ActiveTabIds.GENERAL }
            </div> }
            panel={ <GeneralControlsTab editor={ props.editor }/> }
          />
          <Tab
            disabled={ !shapeTypesWithData.includes(getShapeTypeFromId(props.editor.getSelectedShape()?.pamelaShape__nonObservable.id() as string)) }
            className="outline-none"
            id={ ActiveTabIds.DATA }
            title={ <div className="capitalize">
              <Icon size={ 12 } icon="database"
                    className="bp4-text-muted mr-1 mb-0.5"/>
              { ActiveTabIds.DATA }
            </div> }
            panel={ <div>{ <DataControlsTab editor={ props.editor }/> }</div> }
            panelClassName="ember-panel"
          />
          <Tab
            className="outline-none"
            id={ ActiveTabIds.OPTIONS }
            title={ <div className="capitalize">
              <Icon size={ 12 } icon="wrench"
                    className="bp4-text-muted mr-1 mb-0.5"/>
              { ActiveTabIds.OPTIONS }
            </div> }
            panel={ <OptionsTab editor={ props.editor }/> }
          />
          <Tabs.Expander/>
        </Tabs>
      </div>
    );
  }

  return <NonIdealState
    icon="select"
    className={ 'z-10' }
    title="Nessuna selezione"
    description={ <div className="bp4-text-muted -mt-3">Clicca su un oggetto per
      operarvi</div> }
  />;
});
